import React from 'react';
import { Link } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Tooltip } from '@mui/material';

interface ISingleResultRow {
  key?: number;
  transaction?: any;
  location?: string;
  selectedItems?: any;
  setSelectedItems?: any;
  handleEdit?: any;
  handleDelete?: any;
  viewType?: string;
}

const TariffRow: React.FC<ISingleResultRow> = ({
  key,
  transaction,
  location,
  selectedItems,
  setSelectedItems,
  handleEdit,
  handleDelete,
  viewType,
}) => {
  const handleCheckboxClick = (item: any) => {
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];

      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);

      return alreadySelected
        ? safePrevSelected.filter((i) => i.id !== item.id)
        : [...safePrevSelected, item];
    });
  };

  const formatHTSCode = (code: string | null | undefined): string => {
    if (!code) {
      return '';
    }

    // 1. Remove all non-digit characters (dots, spaces, dashes, etc.)
    const cleanCode = code.toString().replace(/\D/g, '');

    // 2. Check if we have the standard 10-digit HTS code
    if (cleanCode.length === 10) {
      // Format: xxxx.xx.xxxx
      return `${cleanCode.slice(0, 4)}.${cleanCode.slice(
        4,
        6,
      )}.${cleanCode.slice(6)}`;
    }

    // 3. Fallback
    return code;
  };

  // UPDATED: Now calculates based on Product Value, not Total Cost
  const getAdditionalDutyPercent = (
    math: { additionalDutyDollars: number },
    inputs: { productValue: number },
  ): string => {
    const { additionalDutyDollars } = math;
    const { productValue } = inputs;

    // Prevent division by zero
    if (!productValue || productValue === 0) {
      return '0%';
    }

    const percentage = (additionalDutyDollars / productValue) * 100;

    // Format to 2 decimals, removing trailing zeros
    return `${percentage
      .toFixed(2)
      .replace(/\.00$/, '')
      .replace(/(\.\d)0$/, '$1')}%`;
  };

  return (
    <div className='screening-results-row' key={key}>
      <div
        className='screening-results-row-default-cell'
        style={{ maxWidth: '48px' }}
      >
        <div
          className='screening-results-row-default-cell-inner'
          onClick={() => handleCheckboxClick(transaction ? transaction : [])}
        >
          {selectedItems?.some(
            (item: { id: void }) => item.id === transaction?.id,
          ) ? (
            <div className='full-list-filters-cont-row-icon-item'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='12'
                height='12'
                viewBox='0 0 12 12'
                fill='none'
              >
                <path
                  d='M10 3L4.5 8.5L2 6'
                  stroke='var(--text-primary)'
                  strokeWidth='1.6666'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
          ) : (
            <div className='full-list-filters-cont-row-icon-item-unselected'></div>
          )}
        </div>
      </div>
      <div
        className='screening-results-row-default-cell'
        style={{ maxWidth: '115px' }}
      >
        <div className='screening-results-row-default-cell-inner'>
          <Link
            to={{
              pathname: `${location}/${transaction?.id}`,
              state: {
                objectData: {
                  ...transaction,
                  type: location?.includes('products')
                    ? 'products'
                    : location?.includes('supplier')
                    ? 'supplier'
                    : 'tariff',
                  viewType: viewType,
                },
              },
            }}
          >
            <div className='screening-results-row-link-cell-text'>
              {formatHTSCode(transaction?.hts_code)}
            </div>
          </Link>
        </div>
      </div>
      <div
        className='screening-results-row-default-cell'
        style={{ maxWidth: '95px' }}
      >
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            {transaction?.country_of_origin}
          </div>
        </div>
      </div>
      <div
        className='screening-results-row-default-cell'
        style={{ maxWidth: '110px' }}
      >
        <div className='screening-results-row-default-cell-inner'>
          {transaction.latest_tariff_result?.status?.color === 'Green' ? (
            <span className='screening-badge chip-green lens-chip '>Green</span>
          ) : transaction.latest_tariff_result?.status?.color === 'Yellow' ? (
            <Tooltip
              title={transaction.latest_tariff_result?.status?.message}
              placement='top'
            >
              <span className='screening-badge chip-yellow lens-chip '>
                Yellow
              </span>
            </Tooltip>
          ) : (
            transaction.latest_tariff_result?.status?.color === 'Red' && (
              <Tooltip
                title={transaction.latest_tariff_result?.status?.message}
                placement='top'
              >
                <span className='screening-badge chip-red lens-chip '>Red</span>
              </Tooltip>
            )
          )}
        </div>
      </div>
      <div className='screening-results-row-default-cell'>
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            {transaction?.latest_tariff_result?.base?.baseDutyRate || '-'}
          </div>
        </div>
      </div>
      <div className='screening-results-row-default-cell'>
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            {transaction?.latest_tariff_result?.math
              ? getAdditionalDutyPercent(
                  transaction?.latest_tariff_result?.math,
                  transaction?.latest_tariff_result?.inputs,
                )
              : '-'}
          </div>
        </div>
      </div>
      <div className='screening-results-row-default-cell'>
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            $
            {(
              (transaction?.latest_tariff_result?.math?.mpf || 0) +
              (transaction?.latest_tariff_result?.math?.hmf || 0)
            ).toFixed(2)}
          </div>
        </div>
      </div>
      <div className='screening-results-row-default-cell'>
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            $
            {(transaction?.latest_tariff_result?.math?.totalCost || 0).toFixed(
              2,
            )}
          </div>
        </div>
      </div>
      <div className='screening-results-row-default-cell'>
        <div className='screening-results-row-default-cell-inner'>
          <div className='screening-results-row-default-cell-text'>
            {transaction?.product_code || ''}
          </div>
        </div>
      </div>
      <div
        className='screening-results-row-default-cell'
        style={{ maxWidth: '56px', paddingLeft: '0px' }}
      >
        <div className='screening-results-row-default-cell-inner'>
          <EditOutlinedIcon
            onClick={() => handleEdit(transaction ? transaction : [])}
            sx={{ fontSize: 20, color: 'var(--card-text)' }}
          />
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
            onClick={() => handleDelete(transaction ? transaction : [])}
          >
            <path
              d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167'
              stroke='var(--card-text)'
              strokeWidth='1.66667'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default TariffRow;
